import * as React from 'react';
import cn from 'classnames';
import { OpponentSport } from '@common/sport';
import { AgeRange, AgeSchoolLevel, AgeSchoolOptions, CompetitionLevel, Season, Sport } from '@state/modules/team';

import type { IconProps, IconSize } from './icon-prop-types';

import * as Icons from './icons';
import * as Styles from './Icon.module.scss';

const RSVPIconMap = {
    going: Icons.GoingIcon,
    notGoing: Icons.NotGoingIcon,
    noResponse: Icons.NoResponseIcon,
    people: Icons.PeopleIcon,
};

const InviteStatusIconMap = {
    invited: Icons.InvitedIcon,
    notInvited: Icons.NotInvitedIcon,
};

const StatusIconMap = {
    success: Icons.SuccessIcon,
    error: Icons.ErrorIcon,
};

export type Icon = (props: IconProps) => React.JSX.Element;

const SportIconMap: {
    [key in Sport]: Icon;
} = {
    [Sport.BASEBALL]: Icons.BaseballColored,
    [Sport.BASKETBALL]: Icons.BasketballColored,
    [Sport.BOWLING]: Icons.BowlingColored,
    [Sport.CHEERLEADING]: Icons.CheerleadingColored,
    [Sport.ROWING_AND_CREW]: Icons.CrewColored,
    [Sport.CROSS_COUNTY]: Icons.CrossCountryColored,
    [Sport.FIELD_HOCKEY]: Icons.FieldHockeyColored,
    [Sport.FLAG_FOOTBALL]: Icons.FlagFootballColored,
    [Sport.FOOTBALL]: Icons.FootballColored,
    [Sport.GOLF]: Icons.GolfColored,
    [Sport.HOCKEY]: Icons.HockeyColored,
    [Sport.LACROSSE]: Icons.LacrosseColored,
    [Sport.RUGBY]: Icons.RugbyColored,
    [Sport.SOCCER]: Icons.SoccerColored,
    [Sport.SWIMMING_AND_DIVING]: Icons.SwimmingColored,
    [Sport.TENNIS]: Icons.TennisColored,
    [Sport.VOLLEYBALL]: Icons.VolleyballColored,
    [Sport.WATER_POLO]: Icons.WaterPoloColored,
    [Sport.WRESTLING]: Icons.WrestlingColored,
    // Undefined
    [Sport.OTHER]: Icons.JerseyIcon,
    [Sport.SOFTBALL]: Icons.SoftballColored,
    [Sport.TRACK_AND_FIELD]: Icons.CrossCountryColored,
};

const OpponentSportIconMap: {
    [key in OpponentSport]: Icon;
} = {
    [OpponentSport.BASEBALL]: Icons.BaseballGray,
    [OpponentSport.BASKETBALL]: Icons.BasketballGray,
    [OpponentSport.BOWLING]: Icons.BowlingGray,
    [OpponentSport.CHEERLEADING]: Icons.CheerleadingGray,
    [OpponentSport.ROWING_AND_CREW]: Icons.CrewGray,
    [OpponentSport.CROSS_COUNTY]: Icons.CrossCountryGray,
    [OpponentSport.FIELD_HOCKEY]: Icons.FieldHockeyGray,
    [OpponentSport.FLAG_FOOTBALL]: Icons.FlagFootballGray,
    [OpponentSport.FOOTBALL]: Icons.FootballGray,
    [OpponentSport.GOLF]: Icons.GolfGray,
    [OpponentSport.HOCKEY]: Icons.HockeyGray,
    [OpponentSport.LACROSSE]: Icons.LacrosseGray,
    [OpponentSport.RUGBY]: Icons.RugbyGray,
    [OpponentSport.SOCCER]: Icons.SoccerGray,
    [OpponentSport.SWIMMING_AND_DIVING]: Icons.SwimmingGray,
    [OpponentSport.TENNIS]: Icons.TennisGray,
    [OpponentSport.VOLLEYBALL]: Icons.VolleyballGray,
    [OpponentSport.WATER_POLO]: Icons.WaterPoloGray,
    [OpponentSport.WRESTLING]: Icons.WrestlingGray,
    // Undefined
    [OpponentSport.OTHER]: Icons.JerseyStrokeIcon,
    [OpponentSport.SOFTBALL]: Icons.SoftballGray,
    [OpponentSport.TRACK_AND_FIELD]: Icons.CrossCountryGray,
};

const CompetitionLevelIconMap = {
    [CompetitionLevel.SCHOOL]: Icons.GradCapIcon,
    [CompetitionLevel.CLUB_TRAVEL]: Icons.SuitcaseIcon,
    [CompetitionLevel.RECREATIONAL]: Icons.HomeIcon,
};

const AgeRangeIconMap = {
    [AgeRange.OVER_EIGHTEEN]: Icons.MountainsSnowIcon,
    [AgeRange.THIRTEEN_TO_EIGHTEEN]: Icons.MountainsIcon,
    [AgeRange.UNDER_THIRTEEN]: Icons.HillsIcon,
};

const SeasonIconMap = {
    [Season.SPRING]: Icons.FlowerIcon,
    [Season.SUMMER]: Icons.SunIcon,
    [Season.FALL]: Icons.LeafIcon,
    [Season.WINTER]: Icons.SnowflakeIcon,
};

const AgeSchoolLevelIconMap = {
    [AgeSchoolLevel.ELEMENTARY]: Icons.ElementarySchoolIcon,
    [AgeSchoolOptions.HIGH_SCHOOL]: Icons.HighSchoolIcon,
    [AgeSchoolOptions.MIDDLE_SCHOOL]: Icons.MiddleSchoolIcon,
    [AgeSchoolLevel.COLLEGE]: Icons.CollegeIcon,
    [AgeSchoolLevel.MIDDLE_THIRTEEN_AND_OVER]: Icons.MountainsIcon,
    [AgeSchoolLevel.MIDDLE_TWELVE_AND_UNDER]: Icons.HillsIcon,
    [AgeSchoolLevel.HIGH_FRESHMAN]: Icons.FreshmanIcon,
    [AgeSchoolLevel.HIGH_JUNIOR_VARSITY]: Icons.JuniorIcon,
    [AgeSchoolLevel.HIGH_VARSITY]: Icons.VarsityIcon,
};

const VideoControlsIconMap = {
    fullscreenControl: Icons.FullscreenControlIcon,
    minimizeControl: Icons.MinimizeControlIcon,
    playControl: Icons.PlayControlIcon,
    playCircleControl: Icons.PlayCircleIcon,
    pauseControl: Icons.PauseControlIcon,
    volumeControl: Icons.VolumeControlIcon,
    muteControl: Icons.MuteControlIcon,
    autoplayControl: Icons.AutoplayControlIcon,
    skipForwardControl: Icons.SkipForwardControlIcon,
    skipBackwardControl: Icons.SkipBackwardControlIcon,
};

const UtilityIconMap = {
    add: Icons.AddIcon,
    appStarRating: Icons.AppStarRatingIcon,
    appstore: Icons.AppStoreIcon,
    appStoreInverted: Icons.AppStoreInvertedIcon,
    atom: Icons.AtomIcon,
    calendarFilled: Icons.CalendarFilled,
    calendarStroke: Icons.CalendarStrokeIcon,
    capsLock: Icons.CapsLockIcon,
    caretUpDown: Icons.CaretUpDownIcon,
    checkboxEmpty: Icons.CheckboxEmptyIcon,
    checkboxIndeterminate: Icons.CheckboxIndeterminateIcon,
    checkboxSelected: Icons.CheckboxSelectedIcon,
    checkmark: Icons.CheckmarkIcon,
    checkmarkCircle: Icons.CheckmarkCircleIcon,
    checkmarkCircleFilled: Icons.CheckmarkCircleFilledIcon,
    checkmarkFramed: Icons.CheckmarkFramedIcon,
    chevron: Icons.ChevronIcon,
    chevronDoubleLeft: Icons.ChevronDoubleLeftIcon,
    chevronDoubleRight: Icons.ChevronDoubleRightIcon,
    chevronDown: Icons.ChevronDownIcon,
    clip: Icons.ClipIcon,
    clock: Icons.ClockIcon,
    clockStroke: Icons.ClockStrokeIcon,
    close: Icons.CloseIcon,
    closeCircleFilled: Icons.CloseCircleFilledIcon,
    closeFramed: Icons.CloseFramedIcon,
    clubTile: Icons.ClubTileIcon,
    copy: Icons.CopyIcon,
    copyFilled: Icons.CopyFilledIcon,
    copyWithoutBackground: Icons.CopyWithoutBackground,
    curvedArrow: Icons.CurvedArrow,
    delete: Icons.DeleteOutlineIcon,
    dotsHorizontal: Icons.DotsHorizontalIcon,
    dotsVertical: Icons.DotsVerticalIcon,
    download: Icons.DownloadIcon,
    exclamation: Icons.ExclamationIcon,
    eyeFilled: Icons.EyeFilled,
    eyeOffFilled: Icons.EyeClosedIcon,
    filledSuccess: Icons.FilledSuccessIcon,
    fileFilled: Icons.FileFilledIcon,
    filter: Icons.FilterIcon,
    flag: Icons.FlagIcon,
    foamFinger: Icons.FoamFingerIcon,
    gcIcon: Icons.GCIcon,
    gcLogo: Icons.GCLogo,
    gift: Icons.GiftIcon,
    handle: Icons.HandleIcon,
    heart: Icons.HeartIcon,
    import: Icons.ImportIcon,
    info: Icons.InfoIcon,
    inningHalf: Icons.InningHalfIcon,
    jersey: Icons.JerseyIcon,
    jerseyStroke: Icons.JerseyStrokeIcon,
    leagueTile: Icons.LeagueTileIcon,
    leftArrow: Icons.LeftArrow,
    rightArrow: Icons.RightArrow,
    liveVideoIcon: Icons.LiveVideoIcon,
    lock: Icons.LockIcon,
    lockFilled: Icons.LockFilledIcon,
    lockStroke: Icons.LockStrokeIcon,
    megaphoneStroke: Icons.MegaphoneStrokeIcon,
    menu: Icons.MenuIcon,
    minus: Icons.MinusIcon,
    minusFramed: Icons.MinusFramedIcon,
    navigationPointer: Icons.NavigationPointerFilledIcon,
    numLock: Icons.NumLockIcon,
    orgTile: Icons.OrgTileIcon,
    outboundLink: Icons.OutboundLinkIcon,
    phone: Icons.PhoneIcon,
    person: Icons.PersonIcon,
    pin: Icons.PinIcon,
    player: Icons.JerseyStrokeIcon,
    playstore: Icons.PlayStoreIcon,
    playStoreInverted: Icons.PlayStoreInvertedIcon,
    plus: Icons.PlusIcon,
    plusCircle: Icons.PlusCircleIcon,
    plusStroke: Icons.PlusStrokeIcon,
    radioEmpty: Icons.RadioEmptyIcon,
    radioSelected: Icons.RadioSelectedIcon,
    refreshForward: Icons.RefreshForwardIcon,
    remove: Icons.RemoveIcon,
    required: Icons.RequiredIcon,
    roster: Icons.RosterIcon,
    rosterMaxPreps: Icons.RosterMaxPreps,
    rosterNew: Icons.RosterNewIcon,
    rosterPrevious: Icons.RosterPreviousIcon,
    schedule: Icons.CalendarIcon,
    search: Icons.SearchIcon,
    share: Icons.ShareIcon,
    shield: Icons.ShieldIcon,
    sort: Icons.SortIcon,
    star: Icons.StarIcon,
    stars: Icons.StarsIcon,
    stats: Icons.StatsIcon,
    teams: Icons.TeamsIcon,
    ticket: Icons.TicketIcon,
    tournamentTile: Icons.TournamentTileIcon,
    trophy: Icons.TrophyIcon,
    upload: Icons.UploadIcon,
    userPlusStroke: Icons.UserPlusStrokeIcon,
    users: Icons.UsersIcon,
    family: Icons.FamilyIcon,
    videoCamera: Icons.VideoCameraIcon,
    videoCameraOutline: Icons.VideoCameraOutlineIcon,
    whistle: Icons.Whistle,
    winnerArrow: Icons.WinnerArrowIcon,
};

const vendorIconMap = {
    discord: Icons.DiscordIcon,
    facebook: Icons.FacebookIcon,
    reddit: Icons.RedditIcon,
    playstore: Icons.PlayStoreIcon,
    playStoreInverted: Icons.PlayStoreInvertedIcon,
    clip: Icons.ClipIcon,
};

export const sportIconMap = { ...SportIconMap, ...OpponentSportIconMap };

export const otherIconMap = {
    ...RSVPIconMap,
    ...InviteStatusIconMap,
    ...CompetitionLevelIconMap,
    ...AgeRangeIconMap,
    ...SeasonIconMap,
    ...AgeSchoolLevelIconMap,
    ...StatusIconMap,
    ...vendorIconMap,
    ...VideoControlsIconMap,
    ...UtilityIconMap,
};

export const iconMap = {
    ...otherIconMap,
    ...sportIconMap,
};
export const IconLists = Object.keys(iconMap) as IconType[];

export type StatusIcon = keyof typeof StatusIconMap;
export type RSVPStatusIcon = keyof typeof RSVPIconMap;
export type InviteStatusIcon = keyof typeof InviteStatusIconMap;
export type SportIcon = keyof typeof SportIconMap;
export type OpponentSportIcon = keyof typeof OpponentSportIconMap;
export type CompetitionLevelIcon = keyof typeof CompetitionLevelIconMap;
export type AgeRangeIcon = keyof typeof AgeRangeIconMap;
export type SeasonIcon = keyof typeof SeasonIconMap;
export type AgeSchoolLevelIcon = keyof typeof AgeSchoolLevelIconMap;
export type VideoControlsIcon = keyof typeof VideoControlsIconMap;
export type UtilityIcon = keyof typeof UtilityIconMap;
export type IconType = keyof typeof iconMap;

export type IconVariant = 'positive' | 'negative' | 'info';

/*
 * `variant` colors an icon by passing a CSS class down as `className`. Icon.module.scss compiles
 * those rules to a compound selector (`.fill.negative`), so the class only takes effect on an icon
 * that merges `className` into the same element carrying `Styles.fill`. Only these three do: the
 * rest either drop `className` entirely, or put it on the outer <svg> while the fill classes sit on
 * an inner <path>, where the compound selector never matches and the variant silently no-ops.
 * To add an icon here, first update it to merge `className` into its fill class.
 */
type VariantIcon = 'checkmark' | 'close' | 'exclamation';

/*
 * Only VariantIcon accepts `variant`; every other type accepts it as `never`, so misuse is a
 * compile error rather than a silent no-op. The first branch keeps the full IconType so callers
 * passing a dynamically computed type still type-check when they don't ask for a variant.
 */
type Props =
    | (IconProps & { type: IconType; variant?: never })
    | (IconProps & { type: VariantIcon; variant?: IconVariant });

export const Icon = ({ type, variant, fill, stroke, size, width, height, className }: Props) => {
    const Component = iconMap[type];

    if (!Component) {
        return null;
    }

    return (
        <Component
            fill={fill}
            stroke={stroke}
            size={size}
            width={width}
            height={height}
            className={cn(variant && Styles[variant], className) || undefined}
        />
    );
};

export type { IconSize };
